class ScProductMediaRail {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-media-rail-track]');
    this.next = root.querySelector('[data-media-rail-next]');
    if (!this.track || !this.next) return;

    this.track.addEventListener('click', (event) => {
      if (event.target.closest('a, button, video, input, select')) return;
      const card = event.target.closest('[data-media-swap]');
      if (card) this.swapFeaturedMedia(card);
    });

    this.track.addEventListener('keydown', (event) => {
      const card = event.target.closest('[data-media-swap]');
      if (!card || (event.key !== 'Enter' && event.key !== ' ')) return;
      event.preventDefault();
      this.swapFeaturedMedia(card);
    });

    this.next.addEventListener('click', () => {
      this.track.scrollBy({ top: this.track.clientHeight * 0.86, behavior: 'smooth' });
    });

    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        event.preventDefault();
        this.track.scrollBy({ top: this.track.clientHeight * 0.86, behavior: 'smooth' });
      }
      if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        this.track.scrollBy({ top: -this.track.clientHeight * 0.86, behavior: 'smooth' });
      }
    });
  }

  swapFeaturedMedia(card) {
    const gallery = this.root.closest('[data-component="product-media-gallery"]');
    const featured = gallery?.querySelector('.sc-product-hero-media__card--featured');
    if (!gallery || !featured || featured === card) return;

    const marker = document.createComment('media-swap');
    card.replaceWith(marker);
    featured.replaceWith(card);
    marker.replaceWith(featured);

    featured.classList.remove('sc-product-hero-media__card--featured');
    featured.classList.add('sc-product-hero-media__card--supporting');
    featured.removeAttribute('data-media-swap');
    featured.removeAttribute('tabindex');
    featured.removeAttribute('role');
    featured.removeAttribute('aria-label');

    card.classList.remove('sc-product-hero-media__card--supporting');
    card.classList.add('sc-product-hero-media__card--featured');
    card.removeAttribute('data-media-swap');
    card.removeAttribute('tabindex');
    card.removeAttribute('role');
    card.removeAttribute('aria-label');

    featured.setAttribute('data-media-swap', '');
    featured.setAttribute('tabindex', '0');
    featured.setAttribute('role', 'button');
    featured.setAttribute('aria-label', 'View selected product image');
  }
}

class ScProductMediaCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-mobile-media-track]');
    this.slides = [...root.querySelectorAll('[data-mobile-media-slide]')];
    this.realSlides = this.slides;
    this.dots = [...root.querySelectorAll('[data-mobile-media-dot]')];
    if (!this.track || this.realSlides.length < 2) return;

    this.track.addEventListener('scroll', () => this.updateActiveDot(), { passive: true });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.scrollToRealSlide(Number(dot.dataset.mobileMediaDot));
      });
    });

    requestAnimationFrame(() => this.updateActiveDot());
  }

  scrollToRealSlide(index, smooth = true) {
    const slide = this.realSlides[index];
    if (!slide || !this.track) return;

    const targetLeft = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
    this.track.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  updateActiveDot() {
    const trackCenter = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;
    this.realSlides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === closestIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }
}

class ScProductServiceCarousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-service-track]');
    this.slides = [...root.querySelectorAll('[data-service-slide]')];
    this.nextButton = root.querySelector('[data-service-next]');
    this.status = root.querySelector('[data-service-status]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.canHover = window.matchMedia('(hover: hover)').matches;
    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.timer = null;
    this.transitionTimer = null;
    this.hoverPaused = false;
    this.focusPaused = false;
    this.isVisible = true;
    this.isTransitioning = false;
    if (!this.track || !this.nextButton || this.slides.length < 2) return;

    this.nextButton.addEventListener('click', () => this.show(this.currentIndex + 1, true));
    this.nextButton.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.show(this.currentIndex + direction, true);
    });

    root.addEventListener('mouseenter', () => {
      if (!this.canHover) return;
      this.hoverPaused = true;
      this.clearTimer();
    });
    root.addEventListener('mouseleave', () => {
      if (!this.canHover) return;
      this.hoverPaused = false;
      this.schedule();
    });
    root.addEventListener('focusin', () => {
      requestAnimationFrame(() => {
        this.focusPaused = root.matches(':has(:focus-visible)');
        if (this.focusPaused) this.clearTimer();
      });
    });
    root.addEventListener('focusout', () => {
      this.focusPaused = false;
      this.schedule();
    });

    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.clearTimer() : this.schedule();
    });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting;
        this.isVisible ? this.schedule() : this.clearTimer();
      }, { threshold: 0.25 });
      this.observer.observe(root);
    }

    this.updateStatus(false);
    this.schedule();
  }

  isPaused() {
    return this.hoverPaused || this.focusPaused || !this.isVisible || document.hidden;
  }

  clearTimer() {
    window.clearTimeout(this.timer);
    this.timer = null;
  }

  schedule() {
    this.clearTimer();
    if (this.isPaused() || this.isTransitioning) return;
    this.timer = window.setTimeout(() => this.show(this.currentIndex + 1, false), 3000);
  }

  show(index, announce) {
    if (this.isTransitioning) return;
    const nextIndex = (index + this.slides.length) % this.slides.length;
    if (nextIndex === this.currentIndex) return;

    this.clearTimer();
    const current = this.slides[this.currentIndex];
    const next = this.slides[nextIndex];
    this.isTransitioning = true;

    if (this.reducedMotion.matches) {
      current.classList.remove('is-active', 'is-exiting');
      next.classList.add('is-active');
      this.currentIndex = nextIndex;
      this.isTransitioning = false;
      this.updateStatus(announce);
      this.schedule();
      return;
    }

    current.classList.add('is-exiting');
    this.transitionTimer = window.setTimeout(() => {
      current.classList.remove('is-active', 'is-exiting');
      next.classList.add('is-active');
      this.currentIndex = nextIndex;
      this.updateStatus(announce);
      this.transitionTimer = window.setTimeout(() => {
        this.isTransitioning = false;
        this.schedule();
      }, 280);
    }, 220);
  }

  updateStatus(announce) {
    const text = this.slides[this.currentIndex]?.textContent.trim() || '';
    this.nextButton.setAttribute('aria-label', `${text}. Mostra informazione successiva`);
    if (this.status) {
      this.status.setAttribute('aria-live', announce ? 'polite' : 'off');
      this.status.textContent = text;
    }
  }

}

class ScProductValuesSequence {
  static storageKey = 'sc-pdp-values-sequence-v2';

  constructor(root) {
    this.root = root;
    this.items = [...root.querySelectorAll(':scope > div')].slice(0, 3);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (!this.isDesktop || this.reducedMotion || this.items.length !== 3 || this.hasPlayed()) return;

    this.play();
  }

  hasPlayed() {
    try {
      return window.sessionStorage.getItem(ScProductValuesSequence.storageKey) === 'true';
    } catch {
      return false;
    }
  }

  remember() {
    try {
      window.sessionStorage.setItem(ScProductValuesSequence.storageKey, 'true');
    } catch {
      // Storage may be unavailable in private contexts; the visual sequence can still run.
    }
  }

  play() {
    const sequence = document.createElement('div');
    sequence.className = 'sc-pdp-values__sequence';
    sequence.setAttribute('aria-hidden', 'true');

    this.items.forEach((item) => {
      const feature = document.createElement('div');
      feature.className = 'sc-pdp-values__feature';
      [...item.children].forEach((child) => feature.append(child.cloneNode(true)));
      sequence.append(feature);
    });

    this.root.append(sequence);
    this.root.classList.add('is-sequencing');
    this.remember();

    window.setTimeout(() => this.root.classList.add('is-settling'), 2500);
    window.setTimeout(() => {
      this.root.classList.remove('is-sequencing', 'is-settling');
      sequence.remove();
    }, 3300);
  }
}

const initScProductBuyNow = () => {
  if (document.documentElement.dataset.scBuyNowReady === 'true') return;
  document.documentElement.dataset.scBuyNowReady = 'true';

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-sc-buy-now]');
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const form = button.closest('form[action*="/cart/add"]');
    if (!form) return;

    const label = button.querySelector('[data-sc-buy-now-label]');
    const liveRegion = button.closest('product-form-component')?.querySelector('[ref="liveRegion"]');
    const language = document.documentElement.lang.toLowerCase().split('-')[0];
    const messages = {
      en: { buyNow: 'Buy now', opening: 'Opening checkout…', error: 'Unable to proceed to checkout.' },
      fr: { buyNow: 'Acheter maintenant', opening: 'Ouverture du paiement…', error: 'Impossible de poursuivre vers le paiement.' },
      it: { buyNow: 'Acquista ora', opening: 'Apertura checkout…', error: 'Impossibile procedere al checkout.' },
    }[language] || { buyNow: 'Buy now', opening: 'Opening checkout…', error: 'Unable to proceed to checkout.' };
    const defaultLabel = label?.textContent.trim() || messages.buyNow;
    const root = window.Shopify?.routes?.root || '/';
    let isRedirecting = false;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = messages.opening;

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(form),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || messages.error);

      isRedirecting = true;
      window.location.assign(`${root}checkout`);
    } catch (error) {
      if (liveRegion) liveRegion.textContent = error.message;
    } finally {
      if (isRedirecting) return;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (label) label.textContent = defaultLabel;
    }
  });
};

class ScMobilePurchaseBar {
  // Phones: the buttons sit in the page under the price; once they scroll out
  // of view above, they follow as a bar fixed to the bottom of the screen.
  constructor(root) {
    this.bar = root.querySelector('.sc-product-hero__panel .product-form-buttons');
    if (!this.bar) return;

    this.buyNow = this.bar.querySelector(':scope > .sc-buy-now-block');
    this.buyNowSlot = document.createElement('div');
    this.buyNowSlot.className = 'sc-product-hero__mobile-buy-now-slot';
    this.buyNowSlot.setAttribute('data-sc-mobile-buy-now-slot', '');
    this.bar.after(this.buyNowSlot);

    // The spacer holds the bar's place while it is fixed, so nothing jumps;
    // the marker right after it tells when the buttons have scrolled away.
    this.anchor = document.createElement('div');
    this.anchor.className = 'sc-product-hero__buy-anchor';
    this.anchor.setAttribute('aria-hidden', 'true');
    this.bar.before(this.anchor);
    this.marker = document.createElement('div');
    this.marker.className = 'sc-product-hero__buy-marker';
    this.marker.setAttribute('aria-hidden', 'true');
    this.bar.after(this.marker);
    this.passed = false;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        this.passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.update();
      }).observe(this.marker);
    }

    this.update = this.update.bind(this);
    window.addEventListener('resize', this.update, { passive: true });
    this.update();
  }

  update() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    if (this.buyNow) {
      const destination = isMobile ? this.buyNowSlot : this.bar;
      if (this.buyNow.parentElement !== destination) destination.append(this.buyNow);
    }

    const sticky = isMobile && this.passed;
    if (sticky && !this.bar.classList.contains('is-mobile-sticky')) {
      this.anchor.style.height = `${this.bar.offsetHeight}px`;
    }
    if (!sticky) this.anchor.style.height = '';
    this.bar.classList.toggle('is-mobile-sticky', sticky);
  }
}

const initScProductMediaRails = () => {
  document.querySelectorAll('[data-component="product-hero"]').forEach((hero) => {
    new ScMobilePurchaseBar(hero);
  });
  document.querySelectorAll('[data-media-rail]').forEach((rail) => new ScProductMediaRail(rail));
  document.querySelectorAll('[data-component="product-media-carousel"]').forEach((carousel) => new ScProductMediaCarousel(carousel));
  document.querySelectorAll('[data-service-carousel]').forEach((carousel) => new ScProductServiceCarousel(carousel));
  document.querySelectorAll('.sc-product-hero__panel .sc-pdp-values').forEach((values) => new ScProductValuesSequence(values));
  initScProductBuyNow();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScProductMediaRails, { once: true });
} else {
  initScProductMediaRails();
}
